import { _ } from "../../../core/polyglot/polyglot.js";
import { applicableCommands } from "../../../models/accounting/document/entryCommands.js";
import accountingDocument from "../../shared/accountingDocument.js";
import commandWidgetsBuilder from "./commandWidgetsBuilder.js";
import documentArchiveTitleBuilder from "./documentArchiveTitleBuilder.js";
import documentServiceModel from "../../../core/repositories/document/documentService.js";
import HelpWizard from "../../../core/chrome/HelpWizard/HelpWizard.js";
import iconicFolderView from "../widgets/iconicFolderView.js";
import listFolderView from "../widgets/listFolderView.js";
import selection from "../../../models/accounting/document/selection.js";
import selectionEventsManager from "./selectionEventsManager.js";
import session from "../../../core/session.js";
import TitleWidget from "../../../core/chrome/TitleWidget2.js";

/**
 * `foldersDocument` is the entry point of Document Archive.
 * Its goal is to render all entries currently browsed,
 * and to provide contextual actions to manipulate them.
 *
 * It has two different views (list or icons) that the user can select.
 *
 * Its main public API method is `setFolder' that take the id
 * of the current folder to browse and then displays the
 * folder children.
 *
 * Required spec argument:
 * - folder: a `FolderModel` representing the folder to browse
 */

/**
 * Todo: Add JSDoc comment
 */
function foldersDocument(spec, my) {
	my ||= {};
	spec ||= {};

	const that = accountingDocument(spec, my);

	my.documentService = documentServiceModel.forCompany(session.getCompany());

	my.titleBuilder = documentArchiveTitleBuilder();

	my.titleWidget = new TitleWidget({
		title: function (html) {
			html.render(that.title);
		},
		subtitle: function (html) {
			html.render(that.subtitle);
		},
		helpWizard: new HelpWizard({
			stepsBuilder: () => that.help(),
		}),
		helpEnabled: true,
		titleBuilder: my.titleBuilder,
	});

	my.iconicView = iconicFolderView({
		document: that,
		documentService: my.documentService,
	});

	my.listView = listFolderView({
		document: that,
		documentService: my.documentService,
	});

	registerEventsForView(my.listView);
	registerEventsForView(my.iconicView);

	selection.onSelectedItemChanged.register(() => {
		my.titleWidget.updateTitleAndSubtitle();
		my.updateActions();
	});

	my.currentView = my.iconicView;

	that.showEmpty();

	that.hasPeriodPicker = function () {
		return false;
	};

	that.documentId = "folders";
	that.tabLabel = _("Documents");
	that.tabPath = "folder";

	// Title

	that.title = _("Documents");

	that.help = function () {
		return [
			{
				title: _("Documents"),
				content: _(
					"The document archive is a digital archive where your company can store important files concerning the administration of your company.",
				),
			},
			{
				title: _("Folders"),
				content: _(
					"Files can of course be organised in folders. Upload directly to a folder or move files to where they are supposed to be.",
				),
				element: "create-directory",
			},
			{
				title: _("Upload"),
				content: _(
					"Both consultant and client users can upload and download documents to the document archive. Just click the button and browse for a file to share with your partners or administrators.",
				),
				element: "upload",
			},
			{
				title: _("Rights"),
				content: _(
					"It is possible to set read and write rights to folders and files.",
				),
			},
			{
				title: _("Preliminary documents {cur}/{max}", {
					cur: "1",
					max: "3",
				}),
				content: _(
					"Users with the roles Agency director or Consultant have the possibility to mark uploaded documents as being Preliminary. A document with this status is not visible for any client user, even for Company Administrator users with otherwise full access to the document archive.",
				),
				visible: my.canEditPreliminaryDocuments(),
			},
			{
				title: _("Preliminary documents {cur}/{max}", {
					cur: "2",
					max: "3",
				}),
				content: _(
					"To change the status of the documents there are two methods. One way is to go to the Document archive and mark one or several documents and then change status in the right hand menu. An even easier way to get an overview of preliminary documents is to go to Company settings. There you can see a list of all preliminary documents and with one click change the status to visible.",
				),
				visible: my.canEditPreliminaryDocuments(),
			},
			{
				title: _("Preliminary documents {cur}/{max}", {
					cur: "3",
					max: "3",
				}),
				content: _(
					"This functionality makes it possible for the accountant to upload e.g a preliminary annual report and additional documentation. Once the annual report is signed by the CPA the accountant can change the status making the documents visible to the company users.",
				),
				visible: my.canEditPreliminaryDocuments(),
			},
		];
	};

	that.subtitle = _("Shared files and folders");

	// Display the folder provided as argument.
	that.setFolder = function (id) {
		that.showUpdating();

		my.documentService.loadEntries(() => {
			const entry = my.documentService.getChildrenOf(id);
			if (!entry) {
				my.redirectTo("archive");

				my.error(
					_(
						"The selected folder is not accessible or no longer available.",
					),
				);
			}
			my.documentService.setCurrentFolder(entry);
			my.iconicView.setCurrentFolder(entry);
			my.listView.setCurrentFolder(entry);
			my.titleBuilder.setCurrentFolder(entry);
			selection.removeAll();
			that.showContent();
		});
	};

	that.isIconicView = function () {
		return my.currentView === my.iconicView;
	};

	that.isListView = function () {
		return my.currentView === my.listView;
	};

	my.redirectToFolder = function (folder) {
		if (folder.isRoot()) {
			my.redirectTo("archive");
		} else {
			my.redirectTo(`archive/${folder.id()}`);
		}
	};

	my.renderContentOn = function (html) {
		html.render(
			my.section({
				cssClass: "document-archive",
				content: my.currentView,
			}),
		);
	};

	that.renderActionsOn = function (html) {
		const commands = applicableCommands(my.documentService, {
			user: session.getCurrentUser(),
			company: session.getCompany(),
		});

		commands.forEach((command) => {
			command.onRefresh.register(reloadPage);
		});

		const commandWidgets = commandWidgetsBuilder.build(commands);
		html.render(commandWidgets);
	};

	my.canEditPreliminaryDocuments = function () {
		session
			.getCurrentUser()
			.canEditPreliminaryDocuments(session.getCompany());
	};

	function registerEventsForView(view) {
		view.onFileClicked.register(downloadFileFromWidget);
		view.onFolderClicked.register(diveIntoFolderFromWidget);
		view.onSetListView.register(setListView);
		view.onSetIconicView.register(setIconicView);
		view.onEntryWidgetClick.register(entryWidgetClicked);
		view.refreshContents.register(reloadPage);

		view.onNavigateTo.register((folder) => {
			my.redirectToFolder(folder);
		});
	}

	function downloadFileFromWidget(widget, entry) {
		my.documentService.downloadFile({ file: entry });
	}

	function diveIntoFolderFromWidget(widget, entry) {
		my.redirectToFolder(entry);
	}

	function reloadPage() {
		my.documentService.resetCache();
		that.setFolder(my.documentService.getCurrentFolder().id());
	}

	function setIconicView() {
		if (that.isIconicView()) {
			return;
		}
		my.currentView = my.iconicView;
		that.updateDocumentContent();
	}

	function setListView() {
		if (that.isListView()) {
			return;
		}
		my.currentView = my.listView;
		that.updateDocumentContent();
	}

	function entryWidgetClicked(entryWidget) {
		selectionEventsManager.toggleSingleEntrySelection({
			widget: entryWidget,
			entry: entryWidget.model(),
		});
	}

	return that;
}

export default foldersDocument;
